<template>
    <div>
        <!-- Main Detail Modal -->
        <b-modal 
            id="modal-product-detail"
            scrollable 
            :title="modalTitle"
            size="xl"
            hide-footer
            @hidden="resetModal"
            class="product-detail-modal"
            body-class="p-0"
        >
            <div v-if="loading" class="text-center py-5">
                <b-spinner variant="primary" large></b-spinner>
                <p class="mt-2">Loading product details...</p>
            </div>

            <div v-else-if="products && products.length > 0" class="product-detail-wrapper">
                <!-- Column Visibility Toggle Bar -->
                <div class="compact-toolbar mb-3">
                    <div class="toolbar-group">
                        <b-button 
                            :variant="showImageColumn ? 'primary' : 'outline-secondary'" 
                            @click="showImageColumn = !showImageColumn"
                            size="sm"
                        >
                            <b-icon :icon="showImageColumn ? 'eye' : 'eye-slash'"></b-icon>
                            <span class="toolbar-label">IMAGE</span>
                        </b-button>

                        <b-button 
                            :variant="showMainColumns ? 'primary' : 'outline-secondary'" 
                            @click="showMainColumns = !showMainColumns"
                            size="sm"
                        >
                            <b-icon :icon="showMainColumns ? 'eye' : 'eye-slash'"></b-icon>
                            <span class="toolbar-label">MAIN</span>
                        </b-button>

                        <b-button 
                            :variant="showPriceColumns ? 'primary' : 'outline-secondary'" 
                            @click="showPriceColumns = !showPriceColumns"
                            size="sm"
                        >
                            <b-icon :icon="showPriceColumns ? 'eye' : 'eye-slash'"></b-icon>
                            <span class="toolbar-label">PRICE</span>
                        </b-button>

                        <b-button 
                            :variant="showInventoryColumns ? 'primary' : 'outline-secondary'" 
                            @click="showInventoryColumns = !showInventoryColumns"
                            size="sm"
                        >
                            <b-icon :icon="showInventoryColumns ? 'eye' : 'eye-slash'"></b-icon>
                            <span class="toolbar-label">INVENTORY</span>
                        </b-button>

                        <b-button 
                            :variant="showSalesColumns ? 'primary' : 'outline-secondary'" 
                            @click="showSalesColumns = !showSalesColumns"
                            size="sm"
                        >
                            <b-icon :icon="showSalesColumns ? 'eye' : 'eye-slash'"></b-icon>
                            <span class="toolbar-label">SALES</span>
                        </b-button>
                    </div>
                </div>

                <!-- Products Table -->
                <div class="diver">
                    <table class="product-table">
                        <thead>
                            <tr>
                                <th class="srr sticky-col" style="min-width: 40px; width: 40px; background: #fef9e7;">#</th>
                                <th class="srr sticky-col-2" style="min-width: 180px; width: 180px; background: #fef9e7;">SKU</th>
                                <th class="srr" style="min-width: 220px; width: 220px; background: #fef9e7;">PRODUCT NAME</th>
                                <th v-if="showImageColumn" class="srr" style="min-width: 100px; width: 100px; background: #fef9e7;">IMAGE</th>
                                <th v-if="showMainColumns" class="srr" style="min-width: 120px; width: 120px; background: #fef9e7;">CATEGORY</th>
                                <th v-if="showMainColumns" class="srr" style="min-width: 120px; width: 120px; background: #fef9e7;">VENDOR</th>
                                <th v-if="showPriceColumns" class="srr" style="min-width: 100px; width: 100px; background: #ffd1dc;">SELL PRICE</th>
                                <th v-if="showPriceColumns" class="srr" style="min-width: 100px; width: 100px; background: #ffd1dc;">FOB</th>
                                <th v-if="showInventoryColumns" class="srr" style="min-width: 100px; width: 100px; background: #d1ecf1;">QTY</th>
                                <th v-if="showInventoryColumns" class="srr" style="min-width: 100px; width: 100px; background: #d1ecf1;">AVAILABLE</th>
                                <th v-if="showSalesColumns" class="srr" style="min-width: 100px; width: 100px; background: #d4edda;">SOLD</th>
                                <th v-if="showSalesColumns" class="srr" style="min-width: 100px; width: 100px; background: #d4edda;">ORDERED</th>
                                <th class="srr sticky-last" style="min-width: 100px; width: 100px; background: #fef9e7;">ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in products" :key="item.sku">
                                <td class="srrv sticky-col" style="text-align: center;">{{ index + 1 }}</td>
                                <td class="srrv sticky-col-2" style="font-weight: bold;">{{ item.sku }}</td>
                                <td class="srrv" style="text-align: left; padding-left: 10px;">
                                    <span class="product-name">{{ item.product_name || 'N/A' }}</span>
                                    <span v-if="item.discontinued" class="badge badge-danger ml-1">Discontinued</span>
                                    <span v-if="item.is_new_arrival" class="badge badge-success ml-1">New</span>
                                    <span v-if="item.recently_received" class="badge badge-info ml-1">Recent</span>
                                </td>
                                <td v-if="showImageColumn" class="srrv" style="text-align: center;">
                                    <img v-if="getProductImageUrl(item)" 
                                         :src="getProductImageUrl(item)" 
                                         @error="handleImageError"
                                         style="max-height: 50px; max-width: 80px; object-fit: contain;"
                                         class="product-thumb">
                                    <span v-else class="text-muted small">No image</span>
                                </td>
                                <td v-if="showMainColumns" class="srrv">{{ getCategoryName(item.category_id) }}</td>
                                <td v-if="showMainColumns" class="srrv">{{ getVendorName(item.vendor_id) }}</td>
                                <td v-if="showPriceColumns" class="srrv" style="font-weight: bold; color: #28a745;">
                                    ${{ formatPrice(item.selling_price) }}
                                </td>
                                <td v-if="showPriceColumns" class="srrv">${{ formatPrice(item.fob_price) }}</td>
                                <td v-if="showInventoryColumns" class="srrv" style="background: #d1ecf1; font-weight: bold;">
                                    {{ item.total_quantity || 0 }}
                                </td>
                                <td v-if="showInventoryColumns" class="srrv" :style="{ background: getInventoryColor(item.available || 0), fontWeight: 'bold' }">
                                    {{ item.available || 0 }}
                                </td>
                                <td v-if="showSalesColumns" class="srrv" style="background: #d4edda;">{{ item.sold_so_far || 0 }}</td>
                                <td v-if="showSalesColumns" class="srrv" style="background: #d4edda; font-weight: bold; color: #155724;">
                                    {{ item.ordered || 0 }}
                                </td>
                                <td class="srrv sticky-last">
                                    <b-button size="sm" variant="outline-info" @click="viewProductDetail(item)" v-b-tooltip.hover title="View Product">
                                        <b-icon icon="eye" aria-hidden="true"></b-icon>
                                    </b-button>
                                    <b-button size="sm" variant="outline-success" @click="openProductDetail(item)" v-b-tooltip.hover title="Full Details" class="ml-1">
                                        <b-icon icon="grid-3x3-gap-fill" aria-hidden="true"></b-icon>
                                    </b-button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div v-else-if="!loading" class="text-center py-5">
                <b-icon icon="exclamation-triangle" font-scale="3" variant="warning"></b-icon>
                <h5 class="mt-3">No products found</h5>
                <p class="text-muted">No products found for this order</p>
            </div>

            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('modal-product-detail')">
                    Close
                </b-button>
                <b-button variant="primary" @click="refreshData" :disabled="loading">
                    <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                    <b-icon v-else icon="arrow-repeat"></b-icon>
                    Refresh
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import Requestor from "@/utilities/Requestor";

export default {
    name: "ProductDetailModal",
    props: {
        productSku: {
            type: String,
            default: null
        },
        orderData: {
            type: Object,
            default: null
        },
        locationId: {
            type: [String, Number],
            default: null
        }
    },
    data: function() {
        return {
            packet: {},
            products: [],
            loading: false,
            
            // Column visibility
            showImageColumn: true,
            showMainColumns: true,
            showPriceColumns: true,
            showInventoryColumns: true,
            showSalesColumns: true,
        }
    },
    computed: {
        modalTitle() {
            if (!this.orderData) {
                return this.products.length === 1 ? 
                    `${this.products[0].product_name || 'Product'} (${this.products[0].sku})` : 
                    'Product Details';
            }

            // Safely get order info
            const poNumber = this.getOrderField('po_number') || 'N/A';
            const orderType = this.getOrderType();
            
            if (orderType === 'container') {
                const containerNum = this.getOrderField('container_num') || '';
                return `Container Order Details - ${poNumber}${containerNum ? ` (${containerNum})` : ''}`;
            } else if (orderType === 'customer_backorder') {
                return `Customer Backorder Details - ${poNumber}`;
            } else {
                return `Stock Order Details - ${poNumber}`;
            }
        },
        totalAvailable() {
            return this.products.reduce((sum, item) => sum + (parseInt(item.available) || 0), 0);
        },
        totalQuantity() {
            return this.products.reduce((sum, item) => sum + (parseInt(item.total_quantity) || 0), 0);
        },
        totalFobPrice() {
            return this.products.reduce((sum, item) => sum + (parseFloat(item.fob_price) || 0), 0);
        }
    },
    watch: {
        productSku: {
            handler: function(newVal) {
                if (newVal) {
                    this.fetchProductDetails();
                }
            },
            immediate: true
        },
        orderData: {
            handler: function(newVal) {
                if (newVal && newVal.product_skus && newVal.product_skus.length > 0) {
                    this.fetchProductDetails();
                }
            },
            immediate: true,
            deep: true
        },
        locationId: {
            handler: function() {
                if (this.orderData && this.orderData.product_skus && this.orderData.product_skus.length > 0) {
                    this.fetchProductDetails();
                }
            }
        }
    },
    methods: {
        // ===== HELPER METHODS FOR SAFE DATA ACCESS =====
        getOrderField(fieldName) {
            if (!this.orderData) return null;
            
            // Try orderData.data.field (container and stock orders)
            if (this.orderData.data && this.orderData.data[fieldName] !== undefined) {
                return this.orderData.data[fieldName];
            }
            
            // Try orderData.field directly (customer backorders)
            if (this.orderData[fieldName] !== undefined) {
                return this.orderData[fieldName];
            }
            
            // Special mappings for customer backorders
            if (fieldName === 'receiving_location') {
                return this.orderData.receiving_location_id || this.orderData.receiving_location || null;
            }
            if (fieldName === 'bill_to') {
                return this.orderData.bill_to || null;
            }
            if (fieldName === 'ship_to') {
                return this.orderData.ship_to || null;
            }
            if (fieldName === 'vendor_id') {
                return this.orderData.vendor_id || null;
            }
            
            return null;
        },

        getOrderType() {
            if (!this.orderData) return 'unknown';
            
            // Check for container order indicators
            if (this.getOrderField('container_num')) {
                return 'container';
            }
            
            // Check for customer backorder indicators
            if (this.orderData.customer_name || this.orderData.ordering_location !== undefined) {
                return 'customer_backorder';
            }
            
            // Check for stock order indicators
            if (this.orderData.data && this.orderData.data.receiving_location) {
                return 'stock';
            }
            
            // Default to stock
            return 'stock';
        },

        getLocationIdFromOrder() {
            if (!this.orderData) return null;
            
            // Try multiple locations for the location ID
            const locations = [
                this.getOrderField('receiving_location'),
                this.getOrderField('location_id'),
                this.orderData.receiving_location,
                this.orderData.location_id,
                this.orderData.location,
                this.orderData.receiving_location_id
            ];
            
            for (const loc of locations) {
                if (loc !== null && loc !== undefined && loc !== '') {
                    return loc;
                }
            }
            
            return this.locationId || null;
        },

        // ===== FETCH DATA - BATCH VERSION =====
        async fetchProductDetails() {
            this.loading = true;
            
            try {
                let skus = [];
                
                // Get SKUs from order data
                if (this.orderData && this.orderData.product_skus && this.orderData.product_skus.length > 0) {
                    skus = this.orderData.product_skus.map(p => p.product_sku || p.sku).filter(Boolean);
                } else if (this.productSku) {
                    skus = [this.productSku];
                }
                
                if (skus.length === 0) {
                    this.products = [];
                    this.loading = false;
                    return;
                }
                
                // Get the location ID to filter inventory
                const effectiveLocationId = this.getLocationIdFromOrder();
                
                // BATCH FETCH - ONE API CALL FOR ALL PRODUCTS
                const productPath = "/api/product/get_multiple_product_details/";
                const requestData = {
                    skus: skus,
                    location_id: effectiveLocationId
                };
                
                const response = await new Promise((resolve, reject) => {
                    Requestor.postSyncP(productPath, requestData, this, resolve, reject);
                });
                
                if (response.data && response.data.success) {
                    const results = response.data.result || {};
                    const productList = [];
                    
                    for (const sku of skus) {
                        const productData = results[sku];
                        if (productData && productData.product) {
                            const product = productData.product;
                            
                            // Get order item data
                            const orderItem = this.orderData.product_skus.find(p => 
                                (p.product_sku || p.sku) === sku
                            );
                            
                            // Get location-specific inventory
                            const inventoryForLocation = this.getInventoryForLocation(
                                productData.inventory || [],
                                effectiveLocationId
                            );
                            
                            productList.push({
                                sku: product.sku,
                                product_name: product.product_name || 'N/A',
                                category_id: product.category_id,
                                vendor_id: product.vendor_id,
                                selling_price: parseFloat(product.selling_price) || 0,
                                fob_price: parseFloat(product.fob_price) || 0,
                                landed_cost: parseFloat(product.landed_cost) || 0,
                                total_quantity: inventoryForLocation.total_quantity || 0,
                                available: inventoryForLocation.available || 0,
                                sold_so_far: parseInt(product.sold_so_far) || 0,
                                ordered: parseInt(orderItem ? (orderItem.quantity_ordered || orderItem.quantity || 0) : 0),
                                image_url: product.image_url,
                                discontinued: product.discontinued || false,
                                is_new_arrival: product.is_new_arrival || false,
                                recently_received: product.recently_received || false,
                                status: product.status,
                                cubes: product.cubes,
                                spiff: product.spiff,
                                quantity_per_box: product.quantity_per_box || 1
                            });
                        }
                    }
                    
                    this.products = productList;
                    
                    if (this.products.length === 0) {
                        this.$bvToast.toast(
                            "No products found for this order",
                            { title: "Info", variant: "info" }
                        );
                    }
                } else {
                    console.warn('Batch API response error:', response.data);
                    this.$bvToast.toast(
                        response.data.error || "Error loading product details",
                        { title: "Error", variant: "danger" }
                    );
                }
                
            } catch (error) {
                console.error('Error fetching product details:', error);
                this.$bvToast.toast(
                    "Error loading product details. Please try again.",
                    { title: "Error", variant: "danger" }
                );
            } finally {
                this.loading = false;
            }
        },

        getInventoryForLocation(inventory, locationId) {
            if (!locationId || !inventory || inventory.length === 0) {
                return { total_quantity: 0, available: 0 };
            }
            
            // Filter inventory by location ID (string or number comparison)
            const locationInventory = inventory.find(i => {
                const invLocId = i.location_id || i.location || i.locationId;
                return String(invLocId) === String(locationId);
            });
            
            if (locationInventory) {
                return {
                    total_quantity: parseInt(locationInventory.total_quantity) || 0,
                    available: parseInt(locationInventory.available) || 0
                };
            }
            
            return { total_quantity: 0, available: 0 };
        },

        refreshData() {
            this.fetchProductDetails();
        },

        resetModal() {
            this.products = [];
        },

        viewProductDetail(item) {
            this.$emit('view-product', item.sku);
        },

        openProductDetail(item) {
            this.$emit('open-product-detail', item.sku);
        },

        // ===== UTILITY METHODS =====
        getProductImageUrl(product) {
            if (!product || !product.image_url) return null;
            let imageUrl = product.image_url.trim();
            if (imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') return null;
            if (imageUrl.includes('fmapi.myfurnituremecca.com')) {
                return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
            }
            return imageUrl;
        },

        getCategoryName(categoryId) {
            if (this.packet.categoriesdict && categoryId) {
                return this.packet.categoriesdict[categoryId] || 'N/A';
            }
            return 'N/A';
        },

        getVendorName(vendorId) {
            if (this.packet.vendorsdict && vendorId) {
                return this.packet.vendorsdict[vendorId] || 'N/A';
            }
            return 'N/A';
        },

        getInventoryColor(available) {
            const value = parseInt(available) || 0;
            if (value > 10) return '#d4edda';
            if (value > 0) return '#fff3cd';
            return '#f8d7da';
        },

        formatPrice(value) {
            if (value === null || value === undefined) return '0.00';
            const num = parseFloat(value);
            return isNaN(num) ? '0.00' : num.toFixed(2);
        },

        handleImageError(event) {
            event.target.style.display = 'none';
            const parent = event.target.parentElement;
            if (parent) {
                parent.innerHTML = '<span class="text-muted small">No image</span>';
            }
        }
    },
    mounted() {
        this.packet = JSON.parse(localStorage.packet || '{}');
        if (this.productSku || (this.orderData && this.orderData.product_skus && this.orderData.product_skus.length > 0)) {
            this.fetchProductDetails();
        }
    }
}
</script>

<style scoped>
.product-detail-modal >>> .modal-xl {
    max-width: 98%;
}

.product-detail-modal >>> .modal-body {
    padding: 20px;
    background: #f8f9fa;
}

.product-detail-wrapper {
    min-height: 40vh;
    max-height: 75vh;
    overflow-y: auto;
}

/* Compact Toolbar */
.compact-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    background: white;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}

.toolbar-group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
}

.toolbar-group .btn {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.toolbar-group .btn:hover {
    background: #e9ecef !important;
}

.toolbar-group .btn-primary {
    background: #007bff !important;
    color: white !important;
}

.toolbar-group .btn-outline-secondary {
    color: #6c757d;
}

.toolbar-label {
    margin-left: 4px;
    font-size: 10px;
    font-weight: 500;
}

/* Product Table */
.diver {
    overflow: auto;
    width: 100%;
    min-height: 250px;
    max-height: 450px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: white;
}

.product-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 13px;
}

.product-table th,
.product-table td {
    border: 1px solid #dee2e6;
    padding: 6px 8px;
    vertical-align: middle;
    white-space: nowrap;
}

.product-table th {
    position: sticky;
    top: 0;
    z-index: 5;
    background: #f8f9fa;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.srr {
    background: #f8f9fa;
    text-align: center;
}

.srrv {
    text-align: center;
    background: white;
}

.sticky-col {
    position: sticky;
    left: 0;
    z-index: 3;
    background: white;
}

.sticky-col-2 {
    position: sticky;
    left: 40px;
    z-index: 3;
    background: white;
}

.sticky-last {
    position: sticky;
    right: 0;
    z-index: 3;
    background: white;
}

.product-name {
    font-weight: 500;
    white-space: normal;
    word-break: break-word;
}

.product-thumb {
    border-radius: 4px;
    border: 1px solid #dee2e6;
    padding: 2px;
}

.badge {
    font-size: 10px;
    padding: 2px 6px;
}

/* Responsive */
@media (max-width: 768px) {
    .product-table {
        font-size: 11px;
    }
    .product-table th,
    .product-table td {
        padding: 4px 6px;
    }
    .toolbar-label {
        display: none;
    }
    .product-detail-wrapper {
        max-height: 70vh;
    }
}

@media (max-width: 576px) {
    .toolbar-group .btn {
        padding: 2px 6px;
        font-size: 10px;
    }
}

/* Scrollbar */
.product-detail-wrapper::-webkit-scrollbar,
.diver::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

.product-detail-wrapper::-webkit-scrollbar-track,
.diver::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.product-detail-wrapper::-webkit-scrollbar-thumb,
.diver::-webkit-scrollbar-thumb {
    background: #c1c7cd;
    border-radius: 3px;
}

.product-detail-wrapper::-webkit-scrollbar-thumb:hover,
.diver::-webkit-scrollbar-thumb:hover {
    background: #a8afb6;
}
</style>


// WEBPACK FOOTER //
// src/components/VendorOrders/ProductDetailModal.vue